import React, { RefObject, useEffect } from 'react';
import {
  RestaurantDetails_getRestaurantDetails,
  RestaurantDetails_getRestaurantDetails_awards
} from '../../../../../types/graphql-types-generated.ts';
import { Button } from '../../../../../patterns/00-atoms/button/button.tsx';
import { Map } from '../../../../../components/map/map.tsx';
import { UBTracking } from '../../../../../components/tracking/ubtracking.ts';
import { repairUrlProtocol } from '../../../../../utils/url.ts';
import { Icon } from '../../../../../patterns/00-atoms/icon/icon.tsx';
import { FacebookLogo } from '../../../../../patterns/00-atoms/social-media-logos/social-media-logos.tsx';
import { InstagramLogo } from '../../../../../patterns/00-atoms/social-media-logos/social-media-logos.tsx';
import {
  OpeningTimes,
  openingTimesEmpty
} from '../../02-organisms/restaurant-info-modal/opening-times.tsx';
import { Paths } from '../../../../../utils/paths.ts';
import { BrandSkdjSymbol } from '../../../../../patterns/00-atoms/brand/brand-skdj.tsx';
import { nonNullElement } from '../../../../../utils/non-null-element.ts';
import { SiblingRestaurants } from '../../01-molecules/sibling-restaurants/sibling-restaurants.tsx';
import { FiveStarRating } from '../../00-atoms/five-star-rating/five-star-rating.tsx';
import { Ad } from '../../../../page/patterns/00-atoms/ad/ad.tsx';
import { ModalRef } from '../../../../../patterns/01-molecules/modal/modal.tsx';
import { RestaurantInfoModalView } from '../../02-organisms/restaurant-info-modal/restaurant-info-modal.tsx';
import { StickyMenu } from '../../01-molecules/sticky-menu/sticky-menu.tsx';
import { stickyMenuAutoFocus } from '../../01-molecules/sticky-menu/sticky-menu-auto-focus.ts';
import { safeWindow } from '../../../../../utils/window.ts';
import { RestaurantMembershipType } from '../../02-organisms/restaurant-info-modal/membership.tsx';

type TabMoreInfoProps = {
  restaurantDetails: RestaurantDetails_getRestaurantDetails;
  modalRef: RefObject<ModalRef | null>;
  setInfoModalDefaultView?: (view: RestaurantInfoModalView) => void;
  setShowStickyAd?: (show: boolean) => void;
  showAds?: boolean;
};

export const TabMoreInfo: React.FunctionComponent<TabMoreInfoProps> = props => {
  const { restaurantDetails, showAds = true } = props;
  const {
    phone,
    phoneprefix,
    mainOpeningTimes,
    kitchenOpeningTimes,
    description,
    website,
    instagramUrl,
    facebookUrl,
    services,
    awards
  } = restaurantDetails;

  const navigationLink = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${props.restaurantDetails.street} ${props.restaurantDetails.streetnumber}, ${props.restaurantDetails.linkedCity.name}`)}`;
  const reserveLink = `https://www.speisekarte.de${Paths.rdp(props.restaurantDetails)}/tischreservierung`;

  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const itemsContainerRef = React.useRef<HTMLDivElement>(null);
  const navigateButtonRef = React.useRef<HTMLAnchorElement>(null);
  const reserveButtonRef = React.useRef<HTMLAnchorElement>(null);
  const reviewsContainerRef = React.useRef<HTMLDivElement>(null);

  const hasMainOpeningTimes = !openingTimesEmpty(mainOpeningTimes);
  const hasKitchenOpeningTimes = !openingTimesEmpty(kitchenOpeningTimes);

  const stickyMenuItems: { id: string; name: string }[] = [
    {
      id: 'ueber-uns',
      name: 'Über uns'
    },
    {
      id: 'adresse',
      name: 'Adresse'
    },
    hasMainOpeningTimes || hasKitchenOpeningTimes
      ? {
          id: 'oeffnungszeiten',
          name: 'Öffnungszeiten'
        }
      : null,
    {
      id: 'kontakt',
      name: 'Kontakt'
    },
    services.length > 0
      ? {
          id: 'services',
          name: 'Services'
        }
      : null,
    awards && awards.length > 0
      ? {
          id: 'auszeichnungen',
          name: 'Auszeichnungen'
        }
      : null,
    {
      id: 'bewertungen',
      name: 'Bewertungen'
    }
  ].filter((item): item is { id: string; name: string } => item !== null);

  const divider = <div className="bg-sk-grey-200 h-1 md:-mx-4" />;

  const awardEntry = (
    award: RestaurantDetails_getRestaurantDetails_awards | null,
    index: number
  ) => {
    if (award?.type === 'SKDJ_YEAR') {
      return (
        <div key={`${award.type}-${index}`} className="mb-2 flex items-center">
          <BrandSkdjSymbol />
          <span className="text-sk-dark-blue font-bold">{award.title}</span>
        </div>
      );
    } else {
      /* TODO entries for "Varta" and “Der große Guide" */
      return <></>;
    }
  };

  const siblingRestaurants = restaurantDetails.owner?.claims
    .filter(nonNullElement)
    .filter(claim => claim.id !== restaurantDetails.id);

  useEffect(() => {
    const stickyMenuObserver = stickyMenuAutoFocus(itemsContainerRef.current!);

    const win = safeWindow() as Window & {
      moli?: { que: { push: (fn: (adTag: any) => void) => void } };
    };
    win?.moli?.que.push(function (adTag) {
      // adTag.refreshAdSlot(['sk_content_1', 'sk_content_2'], { loaded: 'eager' });
      const ids = document.querySelectorAll('div.h5v-content');
      const adIds = Array.from(ids, element => element.id);
      adTag.refreshAdSlot(adIds, { loaded: 'eager' });
    });

    stickyMenuItems.map(item => {
      const target = document.getElementById(item.id);
      if (target) {
        stickyMenuObserver.observe(target);
      }
    });

    if (mapContainerRef.current) {
      UBTracking.onTrackTargetView(mapContainerRef.current, 'restaurant_info_modal', 'map');
    }

    if (navigateButtonRef.current) {
      UBTracking.onTrackTargetView(
        navigateButtonRef.current,
        'restaurant_info_modal',
        'navigate_to_restaurant',
        'google_maps'
      );
    }

    if (reserveButtonRef.current) {
      UBTracking.onTrackTargetView(
        reserveButtonRef.current,
        'restaurant_info_modal',
        'reservation'
      );
    }

    if (reviewsContainerRef.current) {
      UBTracking.onTrackTargetView(reviewsContainerRef.current, 'restaurant_info_modal', 'reviews');
    }
  }, []);

  return (
    <>
      <StickyMenu
        items={stickyMenuItems}
        onLoad={target => {
          UBTracking.onTrackTargetView(target, 'sticky_menu_more_info', 'more_info_container');
        }}
        onListOpen={() => UBTracking.trackEvent.click('sticky_menu_more_info', 'list', 'open')}
        onListClose={() => UBTracking.trackEvent.click('sticky_menu_more_info', 'list', 'close')}
        onListItemClick={() => UBTracking.trackEvent.click('sticky_menu_more_info', 'item', 'list')}
        onBarItemClick={() => UBTracking.trackEvent.click('sticky_menu_more_info', 'item', 'bar')}
      />

      <div ref={itemsContainerRef}>
        <div className="scroll-m-[108px] p-4 pt-8 md:px-8" id="ueber-uns">
          <h2 className="mb-2 text-xl font-extrabold">Über uns</h2>
          <div className="mb-4">{description}</div>
          <div className="mb-4 flex justify-start">
            {website && (
              <a
                href={repairUrlProtocol(website)}
                className="text-sk-red-500 mr-4 flex items-center text-sm"
                onClick={() =>
                  UBTracking.trackEvent.click('restaurant_info_modal', 'social_media', 'website')
                }
              >
                <Icon name="website" className="mr-1 text-2xl" />
                Website
              </a>
            )}
            {facebookUrl && (
              <FacebookLogo
                variant="red"
                className="mr-4"
                onClick={() =>
                  UBTracking.trackEvent.click('restaurant_info_modal', 'social_media', 'facebook')
                }
                onLoad={target =>
                  UBTracking.onTrackTargetView(
                    target,
                    'restaurant_info_modal',
                    'social_media',
                    'facebook'
                  )
                }
              />
            )}
            {instagramUrl && (
              <InstagramLogo
                variant="red"
                onClick={() =>
                  UBTracking.trackEvent.click('restaurant_info_modal', 'social_media', 'instagram')
                }
                onLoad={target =>
                  UBTracking.onTrackTargetView(
                    target,
                    'restaurant_info_modal',
                    'social_media',
                    'instagram'
                  )
                }
              />
            )}
          </div>
        </div>

        {showAds ? <Ad adId="sk_content_1" minHeightClass="min-h-[480px]" /> : divider}

        <div className="scroll-m-[108px] p-4 md:px-8" id="adresse" ref={mapContainerRef}>
          <div className="text-sm">
            <h2 className="mt-4 text-xl font-extrabold">Adresse</h2>
            <div className="mt-4 flex">
              <div className="flex-grow">
                <div className="font-bold">
                  {restaurantDetails.street} {restaurantDetails.streetnumber}
                </div>
                <div className="font-medium">
                  {restaurantDetails.zip} {restaurantDetails.linkedCity.name}
                </div>
              </div>
              <div>
                <Button ref={navigateButtonRef} href={navigationLink} target="_blank">
                  Navigieren
                </Button>
              </div>
            </div>
            {restaurantDetails.geoLat && restaurantDetails.geoLong && (
              <div className="my-4 overflow-hidden rounded-3xl">
                <Map
                  lat={restaurantDetails.geoLat}
                  lng={restaurantDetails.geoLong}
                  premium={restaurantDetails.membershipType === RestaurantMembershipType.PREMIUM}
                />
              </div>
            )}
          </div>
        </div>

        {showAds ? <Ad adId="sk_content_2" /> : divider}

        <div className="py-4 md:px-4">
          {(hasMainOpeningTimes || hasKitchenOpeningTimes) && (
            <>
              <div className="scroll-m-[108px] p-4" id="oeffnungszeiten">
                {hasMainOpeningTimes && mainOpeningTimes && (
                  <div className="mb-2">
                    <h2 className="mb-4 text-xl font-extrabold">Öffnungszeiten</h2>
                    <OpeningTimes openingTimes={mainOpeningTimes} />
                  </div>
                )}
                {hasKitchenOpeningTimes && kitchenOpeningTimes && (
                  <>
                    <div className="mt-2">
                      <strong>Küche:</strong>
                    </div>
                    <OpeningTimes openingTimes={kitchenOpeningTimes} />
                  </>
                )}
              </div>
              {divider}
            </>
          )}

          <div className="scroll-m-[108px] p-4" id="kontakt">
            <h2 className="my-4 text-xl font-extrabold">Kontakt</h2>
            <div className="mb-4">
              <div>
                <strong>Telefon</strong>
              </div>
              {phoneprefix && <>{phoneprefix}/</>}
              {phone}
            </div>
            <div className="mb-4">
              <Button
                ref={reserveButtonRef}
                href={reserveLink}
                target="_blank"
                onClick={UBTracking.trackEvent.click('restaurant_info_modal', 'reservation')}
              >
                Jetzt reservieren
              </Button>
            </div>
          </div>

          {divider}

          <div className="scroll-m-[108px] py-4 md:px-4" id="services">
            {services.length > 0 && (
              <div className="p-4">
                <h2 className="mb-3 text-xl font-extrabold">Services</h2>
                <div>
                  {services.map(
                    (service, index) =>
                      service && (
                        <span
                          key={`${service.id}-${index}`}
                          className="bg-sk-grey-200 mr-3 mb-2.5 inline-block rounded-[20px] px-2.5 py-1 text-center align-baseline"
                        >
                          {service.name}
                        </span>
                      )
                  )}
                </div>
              </div>
            )}

            {services.length > 0 && awards && awards.length > 0 && divider}

            {awards && awards.length > 0 && (
              <div className="scroll-m-[108px] p-4" id="auszeichnungen">
                {/* TODO entries for "Varta" and “Der große Guide" */}
                {awards.find(a => a?.type === 'SKDJ_YEAR') && (
                  <div className="mt-4 mb-3">
                    <h3 className="mb-3 text-xl font-extrabold">Auszeichnungen</h3>
                    {awards.map(awardEntry)}
                  </div>
                )}
              </div>
            )}
          </div>

          {divider}

          <div className="pt-4 md:-mx-4 md:px-4">
            <div ref={reviewsContainerRef} className="scroll-m-[108px] p-4" id="bewertungen">
              <h2 className="text-xl font-extrabold">Bewertungen</h2>
              <div className="mt-2 flex items-center">
                <span className="text-sk-link-red text-2xl/6">
                  <FiveStarRating rating={props.restaurantDetails.averageReviewScore} />
                </span>
                <span className="ml-2 font-bold">
                  Ø {props.restaurantDetails.averageReviewScore.toFixed(1)}
                </span>
                <span className="ml-1">({props.restaurantDetails.reviewsCount} Bewertungen)</span>
              </div>
              <div className="mt-2">
                <span className="font-bold">
                  Nr. {props.restaurantDetails.rankingScore.rankingInCity} von{' '}
                  {props.restaurantDetails.rankingScore.totalRestaurantsInCity}
                </span>
                <span className="ml-1 font-medium">in</span>
                <a
                  className="text-sk-link-red ml-1 font-medium"
                  href={Paths.cityListing(props.restaurantDetails.linkedCity.fullnameLower)}
                >
                  Restaurants in {props.restaurantDetails.linkedCity.name}
                </a>
              </div>
              <div className="mt-4 mb-2">
                <Button
                  target="_blank"
                  onClick={() => {
                    UBTracking.trackEvent.click('restaurant_info_modal', 'reviews');
                    props.setInfoModalDefaultView &&
                      props.setInfoModalDefaultView({ type: 'reviews' });
                    props.setShowStickyAd && props.setShowStickyAd(true);
                    props.modalRef.current?.openModal();
                  }}
                >
                  Alle Bewertungen
                </Button>
              </div>
            </div>

            {siblingRestaurants && siblingRestaurants.length > 0 && (
              <>
                {divider}
                <SiblingRestaurants
                  restaurantDetails={restaurantDetails}
                  siblings={siblingRestaurants}
                />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};
